import { Row, Divider } from 'antd';
import { groupBy } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type * as api from '../../../../api';
import { useThemeComponents } from '../../../../themes';
import { StyledLocationContainer, LocationTitle, StyledCol } from './shared';

type LocationsProps = {
    module: api.WebsiteModulePublicSpecsFragment;
};

const colSpan = { xxl: 5, xl: 6, lg: 7, md: 12, sm: 24, xs: 24 };
const StyledDivider = styled(Divider)`
    background-color: #4a4e51;
`;
const LocationForMobilityWebContainer = styled.div`
    & .ant-select {
        //Only for Mobility Web Page Selector
        @media screen and (max-width: 1600px) {
            width: 95%;
        }
    }
`;

const Locations = ({ module }: LocationsProps) => {
    const { options } = module;
    const { t } = useTranslation('webpageDetails');
    const { Select } = useThemeComponents();

    const [regions, setRegions] = useState([]);

    const countries = useMemo(() => {
        const countryGrouping = groupBy('country', options.locations);

        return Object.keys(countryGrouping).map(country => ({
            label: country,
            value: country,
            regions: countryGrouping[country].map(option => ({ label: option.region, value: option.url })),
        }));
    }, [options.locations]);

    const onSelectCountry = useCallback(
        value => {
            // 'MY'
            setRegions(countries.find(region => region.value === value).regions);
        },
        [countries]
    );

    const onSelectRegions = useCallback((value, dropdownObject) => {
        window.open(`https://${dropdownObject.value}`, '_blank', 'noopener noreferrer');
    }, []);

    if (!countries?.length) {
        return null;
    }

    return (
        <StyledLocationContainer>
            <LocationTitle level={2}>{t('webpageDetails:country.title')}</LocationTitle>
            <Row>
                <StyledCol {...colSpan}>
                    <LocationForMobilityWebContainer>
                        <Select
                            brightness="dark"
                            onSelect={onSelectCountry}
                            options={countries}
                            placeholder={t('webpageDetails:country.selectRegion')}
                        />
                    </LocationForMobilityWebContainer>
                </StyledCol>

                <StyledCol {...colSpan}>
                    <LocationForMobilityWebContainer>
                        <Select
                            brightness="dark"
                            onSelect={onSelectRegions}
                            options={regions}
                            placeholder={t('webpageDetails:country.selectCountryRegion')}
                        />
                    </LocationForMobilityWebContainer>
                </StyledCol>
            </Row>
            <StyledDivider />
        </StyledLocationContainer>
    );
};

export default Locations;
